import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import { useSelector } from "react-redux";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import { RootState } from "store";
import { AxiosError } from "axios";
import SlideOverPanel from "components/SlideOverPanel";
import AddEmployee from "./AddEmployee";
import EditEmployee from "./EditEmployee";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- INTERFACES ----------

interface RoleType {
  role_id: number;
  role_name: string;
  role_name_raw: string;
}

interface EmployeeType {
  employee_id: number;
  name: string;
  email: string;
  phone?: string;
  is_active: boolean;
  role_names: string;
  id?: number;
}

interface TableMetaData {
  rowIndex: number;
}

// ---------- EXTRACTED COMPONENTS ----------

interface StatusSwitchProps {
  value: boolean;
  data: EmployeeType;
  onStatusChange: (data: EmployeeType, isActive: boolean) => void;
}

const StatusSwitch: React.FC<StatusSwitchProps> = ({
  value,
  data,
  onStatusChange,
}) => (
  <Switch
    checked={value}
    onChange={(event) => onStatusChange(data, event.target.checked)}
    color="primary"
  />
);

interface ActionsCellProps {
  employeeData: EmployeeType;
  onEdit: (employeeId: number) => void;
  onOpenMenu: (
    event: React.MouseEvent<HTMLElement>,
    employee: EmployeeType
  ) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  employeeData,
  onEdit,
  onOpenMenu,
}) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <IconButton
      aria-label="edit"
      onClick={() => onEdit(employeeData.employee_id)}
    >
      <EditIcon />
    </IconButton>
    <IconButton aria-label="swap" onClick={(e) => onOpenMenu(e, employeeData)}>
      <SwapHorizIcon />
    </IconButton>
  </Box>
);

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

const EmployeesList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();

  // Properly typed state
  const [roles, setRoles] = React.useState<RoleType[]>([]);
  const [rolesFilter, setRolesFilter] = React.useState<RoleType | null>(null);
  const [rows, setRows] = React.useState<EmployeeType[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [selectedUser, setSelectedUser] = React.useState<EmployeeType | null>(
    null
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | null>(null);

  const filteredRows = React.useMemo(() => {
    const hasAdminRole = (emp: EmployeeType) =>
      (emp.role_names || "")
        .toLowerCase()
        .split(",")
        .map((s) => s.trim())
        .includes("admin");
    const withoutAdmin = rows.filter((emp) => !hasAdminRole(emp));
    if (!status) return withoutAdmin;
    if (status === "active") {
      return withoutAdmin.filter((c) => Number(c.is_active) === 1);
    }
    if (status === "inactive") {
      return withoutAdmin.filter((c) => Number(c.is_active) === 0);
    }
    return withoutAdmin;
  }, [rows, status]);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    employee: EmployeeType
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(employee);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleStatus = async (data: EmployeeType, isActive: boolean) => {
    try {
      await axios.put(`/employee/is-active/${data.employee_id}`, {
        is_active: isActive,
      });
      fetchData();
      fetchSuccess("Status updated successfully");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleEdit = (employeeId: number) => {
    setSelectedEmployeeId(employeeId);
    setEditPanelOpen(true);
  };

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(`/employee`, {
        params: {
          filters: {
            role_id: rolesFilter?.role_id,
          },
        },
      });
      const { data } = response;

      setRows(data?.list);
    } catch (error) {
      if (error instanceof AxiosError) setError(error);
    }
  }, [rolesFilter]);

  React.useEffect(() => {
    fetchData();
  }, [rolesFilter, fetchData]);

  const fetchRoles = async () => {
    try {
      const rolesData = await axios.get("/roles/all");
      const allRoles = rolesData?.data?.list || [];

      const filteredRoles: RoleType[] = allRoles
        .filter(
          (role: { role_id: number; role_name: string }) =>
            !["admin", "superadmin", "customer"].includes(
              role.role_name.toLowerCase()
            )
        )
        .map((role: { role_id: number; role_name: string }) => ({
          role_id: role.role_id,
          role_name_raw: role.role_name,
          role_name:
            role.role_name.charAt(0).toUpperCase() + role.role_name.slice(1),
        }));

      setRoles(filteredRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  React.useEffect(() => {
    fetchRoles();
  }, []);

  const handleSwapToRole = async (
    userMail: string,
    rawRoleName: string
  ) => {
    try {
      const targetRole = roles.find(
        (r) => r.role_name_raw.toLowerCase() === rawRoleName.toLowerCase()
      );

      const swapData = {
        actualEmail: user?.email,
        actualRole: user?.Role?.[0],
        actualRoleId: user?.roleId,
        actualName: user?.name,
        actualUserId: user?.userId,
        userMail,
        targetRole: targetRole?.role_name_raw ?? rawRoleName,
        targetRoleId: targetRole?.role_id ?? null,
      };

      await axios.post("/auth/switch-user", swapData);
      fetchSuccess(`Switched to ${targetRole?.role_name ?? rawRoleName}`);
      globalThis.location.reload();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      } else {
        fetchError("Failed to swap role");
      }
    }
  };

  // Render functions for table columns
  const renderStatusSwitch = (value: boolean, tableMeta: TableMetaData) => {
    const rowIndex = tableMeta.rowIndex;
    const data = filteredRows[rowIndex];
    return (
      <StatusSwitch value={value} data={data} onStatusChange={handleStatus} />
    );
  };

  const renderActionsCell = (_value: number, tableMeta: TableMetaData) => {
    const rowIndex = tableMeta.rowIndex;
    const employeeData = filteredRows[rowIndex];
    return (
      <ActionsCell
        employeeData={employeeData}
        onEdit={handleEdit}
        onOpenMenu={handleOpenMenu}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "name", label: "Name" },
    { name: "email", label: "Email" },
    { name: "role_names", label: "Role" },
    { name: "phone", label: "Phone" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusSwitch,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Roles
              </SoftTypography>
              <Autocomplete
                options={roles}
                getOptionLabel={(option: RoleType) => option.role_name}
                value={rolesFilter}
                onChange={(_event, value) => setRolesFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={() => setAddPanelOpen(true)}
          >
            Add Member
          </SoftButton>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-employees"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title="Manage Staff Members"
              data={filteredRows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { borderRadius: 2, minWidth: 160 },
          },
        }}
      >
        {selectedUser?.role_names ? (
          selectedUser.role_names.split(",").map((roleName) => {
            const rawRole = roleName.trim();
            const displayRole =
              rawRole.charAt(0).toUpperCase() + rawRole.slice(1);

            return (
              <MenuItem
                key={rawRole}
                onClick={() => {
                  handleSwapToRole(selectedUser.email, rawRole);
                  handleCloseMenu();
                }}
              >
                Swap to {displayRole}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No roles available</MenuItem>
        )}
      </Menu>
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Member"
        width={1000}
      >
        <AddEmployee
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedEmployeeId(null);
        }}
        title="Edit Member"
        width={1000}
      >
        {selectedEmployeeId != null && (
          <EditEmployee
            id={String(selectedEmployeeId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedEmployeeId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedEmployeeId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default EmployeesList;
